import { IProgressTrackerStepProps, PageLoader, ProgressTracker } from "@starrez/starryui";
import { Icons } from "@starrez/starryui9";
import { Formik, FormikActions, FormikProps } from "formik";
import React from "react";
import { IWizardExecuteAllModel, IWizardFormProps, IWizardStep } from ".";
import { ErrorContext, SnackBarContext } from "../../context";
import { AttachmentContext } from "../../context/AttachmentContext/AttachmentContext";
import { strings } from "../../localisation";
import { ButtonBarButton, ButtonBarDiv, ButtonBarLeft, ButtonBarRight } from "../ButtonBar/ButtonBar.styles";
import { Button } from "../Buttons/Button";
import { WizardCloseConfirm } from "../WizardCloseConfirm";
import { WizardCloseSaveConfirm } from "../WizardCloseSaveConfirm/WizardCloseSaveConfirm";
import { WizardContext } from "./Context/WizardContext";
import { ContentDiv, HeaderDiv, WizardForm, WizardLoadingDiv, WizardPanelDiv, WizardTabDiv, WrapperDiv } from "./Wizard.styles";

export enum WizardTypes {
    Email = "Email",
    Template = "Template",
}

export interface IWizardProps {
    IsLoading: boolean;
    Steps: IWizardStep[];
    OnCloseRequest: (isClosing: boolean, shouldSave?: boolean, values?: IWizardExecuteAllModel) => void;
    OnComplete: (values: IWizardExecuteAllModel) => Promise<any>;
    WizardType: WizardTypes;
    FormikRef?: React.RefObject<Formik<IWizardExecuteAllModel>>;
}

export const Wizard = React.memo(({
    IsLoading,
    Steps,
    OnCloseRequest,
    OnComplete,
    WizardType,
    FormikRef }: IWizardProps,
) => {

    // Context
    const { ShouldSaveOnSubmit, UpdateSteps, UpdateSetStep, UpdateSubmitWizard, UpdateIsCompleting } = React.useContext(WizardContext);
    const { IsAttachmentsFinished } = React.useContext(AttachmentContext);
    const errorContext = React.useContext(ErrorContext);
    const { AddMessage } = React.useContext(SnackBarContext);

    // State
    const [currentStepIndex, setcurrentStepIndex] = React.useState(0);
    const [isClosing, setIsClosing] = React.useState(false);
    const [isCompleting, setIsCompleting] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);

    React.useEffect(() => {
        UpdateIsCompleting(isCompleting);
    }, [isCompleting]);

    // Steps
    const isFirstStep: boolean = (currentStepIndex === 0);
    const isLastStep: boolean = (currentStepIndex === Steps.length - 1);
    const currentStep: IWizardStep = Steps[currentStepIndex];

    // Next/Prev buttons
    const nextCaption: string | undefined = isLastStep
        ? strings.getString(`${WizardType}_Action_Wizard_Submit`)
        : currentStep.NextLabel;
    const nextIcon: Icons = !isLastStep
        ? "ArrowRight"
        : WizardType === WizardTypes.Email
            ? "Mail"
            : "Save";

    const prevCaption: string | undefined = currentStep.PreviousLabel;
    const prevIcon: Icons = "ArrowLeft";

    // Wizard / Formik
    const isInitialValid = currentStep.TrackerPanel.props.ValidationSchema.isValidSync(currentStep.TrackerPanel.props.FormikValues);
    const processingCaption = WizardType === WizardTypes.Email ? strings.Message_SendingEmail_ProcessingCaption : strings.Message_SavingTemplate_ProcessingCaption;

    // Init
    React.useEffect(() => {
        if (FormikRef?.current) {
            UpdateSubmitWizard!(() => FormikRef.current!.submitForm);
        }
    }, [FormikRef]);

    React.useEffect(() => {
        UpdateSetStep(() => setcurrentStepIndex);
        UpdateSteps(Steps);
    }, []);

    // Wizard Button Click Events
    const clickStepHandler = (index: number, step: IProgressTrackerStepProps) => {
        if (step.IsLink) {
            setcurrentStepIndex(index);
        }
    };

    const prevClickHandler = (isSubmitting: boolean) => {
        if (!isSubmitting && !isFirstStep && IsAttachmentsFinished && !IsLoading && !isSaving) {
            setcurrentStepIndex(currentStepIndex - 1);
        }
    };

    const nextClickHandler = () => {
        if (isSaving || !IsAttachmentsFinished) {
            return null;
        }
        currentStep.TrackerProps.State = "Complete";
        setcurrentStepIndex(currentStepIndex + 1);
    };

    /*
        This is a callback function that is passed into the Step that allows
        us to perform actions here depending on the result of the validation
    */
    const setHasValidationErrors = (hasErrors: boolean) => {
        FormikRef?.current?.setSubmitting(false);

        if (!hasErrors && FormikRef?.current?.getFormikBag().isValid) {
            nextClickHandler();
        }
    };

    // Progress Tracker
    const progressTrackerSteps = Steps.map((step) => {
        return step.TrackerProps;
    });

    const getStepProps = (step: IWizardStep, formikProps: FormikProps<any>): IWizardFormProps<IWizardExecuteAllModel> => {
        return {
            ...step.TrackerPanel.props,
            IsShown: step === currentStep,
            FormikProps: formikProps,
            SetHasValidationErrors: setHasValidationErrors,
        };
    };

    // Event Handlers
    const handleOnCloseRequest = (shouldClose: boolean) => {
        if (shouldClose) {
            OnCloseRequest(true);
        } else {
            setIsClosing(false);
        }
    };

    const handleOnSubmit = async (
        values: IWizardExecuteAllModel,
        formikHelpers: FormikActions<IWizardExecuteAllModel>): Promise<Promise<void> | void> => {

        /*
            Any Step that has ShouldValidate set to true will kick off an async useEffect in the Step component
            in order to call the validation endpoint. To stop any race conditions from happening, we essentially
            stop this thread here and allow the useEffect to carry out the rest of the work.
         */
        if (FormikRef!.current?.getFormikBag().isSubmitting && !currentStep.TrackerPanel.props.ShouldValidate) {
            if (!IsAttachmentsFinished || IsLoading) {
                formikHelpers.setSubmitting(false);
            } else if (isLastStep || ShouldSaveOnSubmit) {

                currentStep.TrackerProps.State = "Complete";
                setIsCompleting(true);

                OnComplete(values)
                    .then(() => {
                        OnCloseRequest(true);
                    }, () => {
                        formikHelpers.setSubmitting(false);
                        setIsCompleting(false);
                    });

            } else {
                nextClickHandler();
                formikHelpers.setSubmitting(false);
            }
        }
    };

    const handleOnSave = (formikProps: FormikProps<IWizardExecuteAllModel>) => {
        if (!formikProps.dirty) {
            AddMessage?.({
                Caption: strings.Message_Wizard_TemplateSave_NoChanges,
                NotificationType: "Info",
            });
            return;
        }

        if (!formikProps.isValid) {
            return;
        }
        setIsSaving(true);
        UpdateIsCompleting(true);

        OnComplete(formikProps.values)
            .then(() => {
                UpdateIsCompleting(false);
                setIsSaving(false);
                formikProps.resetForm(formikProps.values);
                AddMessage?.({
                    Caption: strings.Message_Wizard_TemplateSave_Success,
                    NotificationType: "Success",
                    Variant: "Bold",
                });
            }).catch((error) => {
                UpdateIsCompleting(false);
                setIsSaving(false);
                errorContext.SetError(error);
            });
    };

    const handleCancelClick = (formikProps: FormikProps<IWizardExecuteAllModel>) => {
        if (formikProps.isSubmitting || isSaving) {
            return;
        }

        if (WizardType === WizardTypes.Email) {
            setIsClosing(true);
        }

        if (WizardType === WizardTypes.Template) {
            formikProps.dirty ? setIsClosing(true) : handleOnCloseRequest(true);
        }
    };

    // Render Functions
    const getClosingPopUp = () => {
        if (!isClosing) {
            return null;
        }

        if (WizardType === WizardTypes.Template) {
            return <WizardCloseSaveConfirm SetIsClosing={handleOnCloseRequest} />;
        }

        return <WizardCloseConfirm SetIsClosing={handleOnCloseRequest} />;
    };

    return (
        <WrapperDiv>

            {getClosingPopUp()}

            <Formik
                enableReinitialize={true}
                initialValues={currentStep.TrackerPanel.props.FormikValues}
                isInitialValid={isInitialValid}
                onSubmit={handleOnSubmit}
                ref={FormikRef}
                validateOnBlur={true}
                validateOnChange={true}
                validationSchema={currentStep.TrackerPanel.props.ValidationSchema}
            >
                {(formikProps: FormikProps<IWizardExecuteAllModel>) => {

                    return (
                        <WizardForm
                            onSubmit={(e) => formikProps.isSubmitting
                                ? e.preventDefault()
                                : formikProps.handleSubmit(e)}
                        >
                            <HeaderDiv>
                                <ProgressTracker
                                    CurrentStep={currentStepIndex}
                                    Steps={progressTrackerSteps}
                                    OnClickStep={clickStepHandler}
                                />
                            </HeaderDiv>
                            {isCompleting || IsLoading ?
                                <WizardLoadingDiv>
                                    <PageLoader Caption={isCompleting ? processingCaption : ""} />
                                </WizardLoadingDiv>
                                :
                                <ContentDiv>
                                    <WizardPanelDiv
                                        role="tabpanel"
                                        id={currentStep.TrackerProps.AriaControlsPanel}
                                        IsSaving={isSaving}
                                    >
                                        {Steps.map((step) => {
                                            const stepProps = getStepProps(step, formikProps);
                                            return (
                                                <WizardTabDiv Hidden={!stepProps.IsShown} key={step.TrackerProps.Title} aria-expanded={stepProps.IsShown} id={`${step.TrackerProps.ClientId}-content`}>
                                                    {React.cloneElement(step.TrackerPanel, { ...stepProps })}
                                                </WizardTabDiv>
                                            );
                                        })}
                                    </WizardPanelDiv>
                                </ContentDiv>
                            }
                            {!currentStep.HideButtonBar &&
                                <ButtonBarDiv>
                                    <ButtonBarLeft>
                                        <ButtonBarButton>
                                            { /* Cancel */}
                                            <Button
                                                variant="Tertiary"
                                                onClick={() => handleCancelClick(formikProps)}
                                                showAsEnabled={!formikProps.isSubmitting && !isSaving}
                                            >
                                                {strings.Action_Cancel}
                                            </Button>
                                        </ButtonBarButton>
                                        {!isFirstStep && (
                                            <ButtonBarButton>
                                                { /* Previous */}
                                                <Button
                                                    variant="Tertiary"
                                                    onClick={() => prevClickHandler(formikProps.isSubmitting)}
                                                    showAsEnabled={!isFirstStep && !formikProps.isSubmitting && IsAttachmentsFinished && !isSaving}
                                                    icon={prevIcon}
                                                >
                                                    {prevCaption}
                                                </Button>
                                            </ButtonBarButton>
                                        )}
                                    </ButtonBarLeft>
                                    <ButtonBarRight>
                                        {!isLastStep && WizardType === WizardTypes.Template && (
                                            <ButtonBarButton>
                                                { /* Save */}
                                                <Button
                                                    variant="Tertiary"
                                                    onClick={() => handleOnSave(formikProps)}
                                                    showAsEnabled={formikProps.dirty && formikProps.isValid}
                                                >
                                                    {strings.Action_Save}
                                                </Button>
                                            </ButtonBarButton>
                                        )}
                                        <ButtonBarButton>
                                            { /* Next */}
                                            <Button
                                                variant="Primary"
                                                type="submit"
                                                showAsEnabled={formikProps.isValid && !formikProps.isSubmitting && IsAttachmentsFinished && !isSaving}
                                                icon={nextIcon}
                                                iconPosition={"Right"}
                                            >
                                                {nextCaption}
                                            </Button>
                                        </ButtonBarButton>
                                    </ButtonBarRight>
                                </ButtonBarDiv>
                            }
                        </WizardForm>

                    );
                }}
            </Formik>
        </WrapperDiv>
    );
}, (prevProps, nextProps) => prevProps.IsLoading === nextProps.IsLoading);
